import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Clock, Plus, Edit2, Trash2, Calendar as CalendarIcon, Loader2, User as UserIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getVolunteerProfileByUserId,
  createVolunteerProfile,
  updateVolunteerProfile,
} from '@/lib/firebase/queries/volunteerProfiles';
import {
  getAllVolunteerHours,
  addVolunteerHours,
  updateVolunteerHours,
  deleteVolunteerHours,
} from '@/lib/firebase/queries/volunteerHours';
import type { User } from '@/types';
import type { VolunteerProfile, VolunteerHour } from '@/types/volunteer';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface UserVolunteeringProps {
  user: User;
  onUpdate?: () => void;
}

export function UserVolunteering({ user, onUpdate }: UserVolunteeringProps) {
  const { t, dir, language } = useLanguage();
  const { currentUser } = useAuth();
  const isRTL = dir === 'rtl';

  // Profile state
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const [fullNameEn, setFullNameEn] = useState('');
  const [fullNameAr, setFullNameAr] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [scfhsNumber, setScfhsNumber] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('');
  const [volunteerAreas, setVolunteerAreas] = useState('');

  // Hours state
  const [loadingHours, setLoadingHours] = useState(true);
  const [volunteerHours, setVolunteerHours] = useState<VolunteerHour[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingHour, setEditingHour] = useState<VolunteerHour | null>(null);
  const [hourToDelete, setHourToDelete] = useState<VolunteerHour | null>(null);
  const [savingHours, setSavingHours] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Hour form fields
  const [hours, setHours] = useState<string>('');
  const [date, setDate] = useState<Date>(new Date());
  const [activityEn, setActivityEn] = useState('');
  const [activityAr, setActivityAr] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionAr, setDescriptionAr] = useState('');

  useEffect(() => {
    loadProfile();
    loadVolunteerHours();
  }, [user.id]);

  const loadProfile = async () => {
    try {
      setLoadingProfile(true);
      const existingProfile = await getVolunteerProfileByUserId(user.id);
      
      if (existingProfile) {
        setProfile(existingProfile);
        setFullNameEn(existingProfile.fullNameEn || existingProfile.fullName);
        setFullNameAr(existingProfile.fullNameAr || '');
        setEmail(existingProfile.email);
        setPhone(existingProfile.phone);
        setNationalId(existingProfile.nationalId);
        setScfhsNumber(existingProfile.scfhsNumber || '');
        setEmploymentStatus(existingProfile.employmentStatus);
        setVolunteerAreas(existingProfile.volunteerAreas || '');
      } else {
        // Pre-fill with user data
        setFullNameEn(`${user.firstNameEn} ${user.lastNameEn}`);
        setFullNameAr(`${user.firstNameAr} ${user.lastNameAr}`);
        setEmail(user.email);
        setPhone(user.phone || '');
      }
    } catch (error) {
      console.error('Error loading volunteer profile:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.loadProfile'));
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadVolunteerHours = async () => {
    try {
      setLoadingHours(true);
      const allHours = await getAllVolunteerHours();
      const userHours = allHours.filter(hour => hour.volunteerId === user.id);
      setVolunteerHours(userHours);
    } catch (error) {
      console.error('Error loading volunteer hours:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.loadHours'));
    } finally {
      setLoadingHours(false);
    }
  };

  const handleSaveProfile = async () => {
    // Validation
    if (!fullNameEn || !fullNameAr || !email || !phone || !nationalId || !employmentStatus) {
      toast.error(t('dashboard.admin.users.volunteering.errors.fillRequired'));
      return;
    }

    if (!/^05\d{8}$/.test(phone)) {
      toast.error(t('dashboard.admin.users.volunteering.errors.invalidPhone'));
      return;
    }

    if (!/^\d{10}$/.test(nationalId)) {
      toast.error(t('dashboard.admin.users.volunteering.errors.invalidNationalId'));
      return;
    }

    try {
      setSavingProfile(true);
      
      if (profile) {
        await updateVolunteerProfile(profile.id, {
          fullName: language === 'ar' ? fullNameAr : fullNameEn,
          fullNameEn,
          fullNameAr,
          email,
          phone,
          nationalId,
          scfhsNumber: scfhsNumber || undefined,
          employmentStatus,
          volunteerAreas: volunteerAreas || undefined,
        });
        toast.success(t('dashboard.admin.users.volunteering.success.profileUpdated'));
      } else {
        await createVolunteerProfile({
          userId: user.id,
          fullName: language === 'ar' ? fullNameAr : fullNameEn,
          fullNameEn,
          fullNameAr,
          email,
          phone,
          nationalId,
          scfhsNumber: scfhsNumber || undefined,
          employmentStatus,
          volunteerAreas: volunteerAreas || undefined,
        });
        toast.success(t('dashboard.admin.users.volunteering.success.profileCreated'));
      }
      
      await loadProfile();
      onUpdate?.();
    } catch (error) {
      console.error('Error saving volunteer profile:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.saveProfile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddHours = async () => {
    if (!hours || !activityEn || !activityAr || !currentUser) {
      toast.error(t('dashboard.admin.users.volunteering.errors.fillRequired'));
      return;
    }

    try {
      setSavingHours(true);

      await addVolunteerHours({
        volunteerId: user.id,
        hours: parseFloat(hours),
        date,
        activity: language === 'ar' ? activityAr : activityEn,
        activityAr,
        activityEn,
        description: language === 'ar' ? descriptionAr : descriptionEn,
        descriptionAr,
        descriptionEn,
        addedBy: currentUser.uid,
      });

      toast.success(t('dashboard.admin.users.volunteering.success.hoursAdded'));
      setAddDialogOpen(false);
      resetHourForm();
      await loadVolunteerHours();
    } catch (error) {
      console.error('Error adding hours:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.addHours'));
    } finally {
      setSavingHours(false);
    }
  };

  const handleEditHours = async () => {
    if (!editingHour || !hours || !activityEn || !activityAr) {
      toast.error(t('dashboard.admin.users.volunteering.errors.fillRequired'));
      return;
    }

    try {
      setSavingHours(true);

      await updateVolunteerHours(editingHour.id, {
        hours: parseFloat(hours),
        date,
        activity: language === 'ar' ? activityAr : activityEn,
        activityAr,
        activityEn,
        description: language === 'ar' ? descriptionAr : descriptionEn,
        descriptionAr,
        descriptionEn,
        volunteerId: editingHour.volunteerId,
        addedBy: editingHour.addedBy,
      });

      toast.success(t('dashboard.admin.users.volunteering.success.hoursUpdated'));
      setEditDialogOpen(false);
      setEditingHour(null);
      resetHourForm();
      await loadVolunteerHours();
    } catch (error) {
      console.error('Error updating hours:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.updateHours'));
    } finally {
      setSavingHours(false);
    }
  };

  const handleDeleteHours = async () => {
    if (!hourToDelete) return;

    try {
      setDeleting(true);
      await deleteVolunteerHours(hourToDelete.id);
      toast.success(t('dashboard.admin.users.volunteering.success.hoursDeleted'));
      setDeleteDialogOpen(false);
      setHourToDelete(null);
      await loadVolunteerHours();
    } catch (error) {
      console.error('Error deleting hours:', error);
      toast.error(t('dashboard.admin.users.volunteering.errors.deleteHours'));
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (hour: VolunteerHour) => {
    setEditingHour(hour);
    setHours(hour.hours.toString());
    setDate(new Date(hour.date));
    setActivityEn(hour.activityEn);
    setActivityAr(hour.activityAr);
    setDescriptionEn(hour.descriptionEn || '');
    setDescriptionAr(hour.descriptionAr || '');
    setEditDialogOpen(true);
  };

  const resetHourForm = () => {
    setHours('');
    setDate(new Date());
    setActivityEn('');
    setActivityAr('');
    setDescriptionEn('');
    setDescriptionAr('');
  };

  const getTotalHours = () => {
    return volunteerHours.reduce((sum, hour) => sum + hour.hours, 0);
  };

  if (loadingProfile || loadingHours) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      {/* Volunteer Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.admin.users.volunteering.profileTitle') || 'Volunteer Profile'}</CardTitle>
          <CardDescription>
            {t('dashboard.admin.users.volunteering.profileDesc') || 'Manage volunteer registration information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullNameEn">Full Name (English) *</Label>
              <Input
                id="fullNameEn"
                value={fullNameEn}
                onChange={(e) => setFullNameEn(e.target.value)}
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullNameAr">Full Name (Arabic) *</Label>
              <Input
                id="fullNameAr"
                value={fullNameAr}
                onChange={(e) => setFullNameAr(e.target.value)}
                placeholder="أحمد محمد"
                dir="rtl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="05XXXXXXXX"
                maxLength={10}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nationalId">National ID *</Label>
              <Input
                id="nationalId"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value)}
                placeholder="1234567890"
                maxLength={10}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="scfhsNumber">SCFHS Number</Label>
              <Input
                id="scfhsNumber"
                value={scfhsNumber}
                onChange={(e) => setScfhsNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employmentStatus">Employment Status *</Label>
              <Select value={employmentStatus} onValueChange={setEmploymentStatus}>
                <SelectTrigger id="employmentStatus">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">Employed</SelectItem>
                  <SelectItem value="unemployed">Unemployed</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="volunteerAreas">Volunteer Areas</Label>
              <Textarea
                id="volunteerAreas"
                value={volunteerAreas}
                onChange={(e) => setVolunteerAreas(e.target.value)}
                placeholder="Areas of interest for volunteering..."
                rows={3}
              />
            </div>
          </div>

          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} pt-4`}>
            <Button onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('common.saving') || 'Saving...'}
                </>
              ) : (
                t('common.save') || 'Save Profile'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Volunteer Hours Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('dashboard.admin.users.volunteering.hoursTitle') || 'Volunteer Hours'}</CardTitle>
              <CardDescription>
                {t('dashboard.admin.users.volunteering.hoursDesc') || 'Track and manage volunteer hours'}
              </CardDescription>
            </div>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('dashboard.admin.volunteers.addHours') || 'Add Hours'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Total Hours Badge */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Total Hours:</span>
              <Badge variant="default" className="text-lg px-3 py-1">
                {getTotalHours().toFixed(1)}h
              </Badge>
            </div>
          </div>

          {/* Hours Table */}
          {volunteerHours.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">No volunteer hours recorded</h3>
              <p className="text-sm text-muted-foreground">Add the first volunteer hours to get started</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Added By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {volunteerHours.map((hour) => (
                    <TableRow key={hour.id}>
                      <TableCell>{format(new Date(hour.date), 'PPP')}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{hour.hours}h</Badge>
                      </TableCell>
                      <TableCell>{language === 'ar' ? hour.activityAr : hour.activityEn}</TableCell>
                      <TableCell className="max-w-[200px]">
                        <span className="line-clamp-2">
                          {language === 'ar' ? hour.descriptionAr : hour.descriptionEn || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{hour.addedByName}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(hour)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setHourToDelete(hour);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Hours Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px]" dir={dir}>
          <DialogHeader>
            <DialogTitle>Add Volunteer Hours</DialogTitle>
            <DialogDescription>Record volunteer hours for this user</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="hours">Hours *</Label>
                <Input
                  id="hours"
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="8"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('justify-start text-left font-normal', !date && 'text-muted-foreground')}>
                      <CalendarIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {date ? format(date, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="activityEn">Activity (English) *</Label>
              <Input
                id="activityEn"
                placeholder="Community service, Event organization..."
                value={activityEn}
                onChange={(e) => setActivityEn(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="activityAr">Activity (Arabic) *</Label>
              <Input
                id="activityAr"
                placeholder="خدمة المجتمع، تنظيم فعاليات..."
                value={activityAr}
                onChange={(e) => setActivityAr(e.target.value)}
                dir="rtl"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="descriptionEn">Description (English)</Label>
              <Textarea
                id="descriptionEn"
                placeholder="Additional details..."
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="descriptionAr">Description (Arabic)</Label>
              <Textarea
                id="descriptionAr"
                placeholder="تفاصيل إضافية..."
                value={descriptionAr}
                onChange={(e) => setDescriptionAr(e.target.value)}
                rows={3}
                dir="rtl"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={savingHours}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleAddHours} disabled={savingHours}>
              {savingHours ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('common.saving') || 'Saving...'}
                </>
              ) : (
                t('common.save') || 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Hours Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]" dir={dir}>
          <DialogHeader>
            <DialogTitle>Edit Volunteer Hours</DialogTitle>
            <DialogDescription>Update volunteer hours record</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-hours">Hours *</Label>
                <Input
                  id="edit-hours"
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="8"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('justify-start text-left font-normal', !date && 'text-muted-foreground')}>
                      <CalendarIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {date ? format(date, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-activityEn">Activity (English) *</Label>
              <Input
                id="edit-activityEn"
                placeholder="Community service, Event organization..."
                value={activityEn}
                onChange={(e) => setActivityEn(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-activityAr">Activity (Arabic) *</Label>
              <Input
                id="edit-activityAr"
                placeholder="خدمة المجتمع، تنظيم فعاليات..."
                value={activityAr}
                onChange={(e) => setActivityAr(e.target.value)}
                dir="rtl"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-descriptionEn">Description (English)</Label>
              <Textarea
                id="edit-descriptionEn"
                placeholder="Additional details..."
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-descriptionAr">Description (Arabic)</Label>
              <Textarea
                id="edit-descriptionAr"
                placeholder="تفاصيل إضافية..."
                value={descriptionAr}
                onChange={(e) => setDescriptionAr(e.target.value)}
                rows={3}
                dir="rtl"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={savingHours}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleEditHours} disabled={savingHours}>
              {savingHours ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('common.saving') || 'Saving...'}
                </>
              ) : (
                t('common.save') || 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Volunteer Hours</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this volunteer hours record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('common.cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteHours}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('common.deleting') || 'Deleting...'}
                </>
              ) : (
                t('common.delete') || 'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

